import {
    StrategyIndicatorInstance,
    StrategyInputNode,
    StrategyNodeAttributeTemplate,
    StrategyNodeTemplate,
} from '~/api-services/AIStrategyBuilderService/types';

// Strip the rule/optimisation namespace from a node/ref name, e.g. "r.1.SMA.short" -> "SMA.short",
// "o.trend.PRICE.TREND" -> "PRICE.TREND", "r.1.1.if.buy" -> "if.buy". Tolerates the legacy
// bare-number prefix ("1.SMA.short").
export const stripPrefix = (name: string): string => name.replace(/^(?:r\.\d+\.|o\.[^.]+\.|\d+\.)(?:\d+\.)?/, '');

// The optimisation code embedded in a node name ("o.<code>.*"), or null for non-optimisation nodes.
export const optimisationCodeOf = (name: string): string | null => {
    const match = /^o\.([^.]+)\./.exec(name);
    return match ? match[1] : null;
};

export const isRange = (value: unknown): value is { min: number; max: number } =>
    !!value &&
    typeof value === 'object' &&
    typeof (value as Record<string, unknown>).min === 'number' &&
    typeof (value as Record<string, unknown>).max === 'number';

// Render a single attribute value: numbers/strings as-is, optimizer {min,max} ranges as
// "min – max", anything else as compact JSON.
export const formatAttrValue = (value: unknown): string => {
    if (value === null || value === undefined || value === '') {
        return '—';
    }
    if (typeof value === 'number' || typeof value === 'string') {
        return String(value);
    }
    if (isRange(value)) {
        return `${value.min} – ${value.max}`;
    }
    if (typeof value === 'object') {
        return JSON.stringify(value);
    }
    return String(value);
};

export type NodeGroup = { indicators: StrategyInputNode[]; connections: StrategyInputNode[] };
export type RuleGroup = NodeGroup & { num: number };
export type OptimisationGroup = NodeGroup & { code: string };

export type NodeDescriber = {
    container: StrategyInputNode | null;
    ruleGroups: RuleGroup[];
    optimisationGroups: OptimisationGroup[];
    fieldsForNode: (node: StrategyInputNode) => StrategyNodeAttributeTemplate[] | null;
    nodeLabel: (node: StrategyInputNode) => string;
    describeConnection: (conn: StrategyInputNode) => string;
};

/**
 * Parse a stored strategy node array (input.strategy or response.optimal_strategy) into the
 * shapes the read-only views render: the CLOUD container, rules grouped by their "r.<n>."
 * namespace, optimisations grouped by their "o.<code>." namespace, and human-readable
 * connection descriptions ("CrossoverIF SMA short.sma crosses above SMA long.sma").
 * Metadata (indicator instances / node templates) is best-effort: without it, labels and
 * fields fall back to the node's own keys. Extracted verbatim from the PoC ViewStrategyNodes.
 */
export function createNodeDescriber(
    nodes: StrategyInputNode[],
    indicatorInstances: Record<string, StrategyIndicatorInstance>,
    nodeTemplates: Record<string, StrategyNodeTemplate>
): NodeDescriber {
    // Authoritative template field list for a node: instance fields take precedence (they
    // carry preset hidden/readonly flags), then the base node-type template, else null
    // (caller falls back to the node's own keys).
    const fieldsForNode = (node: StrategyInputNode): StrategyNodeAttributeTemplate[] | null => {
        const code = stripPrefix(String(node.name ?? ''));
        const instFields = indicatorInstances[code]?.fields;
        if (instFields && instFields.length > 0) {
            return instFields;
        }
        const tmplFields = node.type ? nodeTemplates[String(node.type)]?.fields : undefined;
        if (tmplFields && tmplFields.length > 0) {
            return tmplFields;
        }
        return null;
    };

    const nodeLabel = (node: StrategyInputNode): string => {
        const code = stripPrefix(String(node.name ?? ''));
        const inst = indicatorInstances[code];
        const tmpl = node.type ? nodeTemplates[String(node.type)] : undefined;
        return inst?.shortDisplayName ?? inst?.displayName ?? tmpl?.shortName ?? code;
    };

    // Classify generically from template metadata — no hardcoded type strings.
    const isContainer = (n: StrategyInputNode) => 'exitCondition' in n || 'enterExpression' in n;
    const hasRefFields = (n: StrategyInputNode) => {
        const fields = fieldsForNode(n);
        if (fields) {
            return fields.some((f) => f.referencesNode);
        }
        return typeof n.x === 'string' && typeof n.y === 'string';
    };
    const container = nodes.find(isContainer) ?? null;
    const connections = nodes.filter((n) => n !== container && hasRefFields(n));
    const indicators = nodes.filter((n) => n !== container && !connections.includes(n));

    const isOptNode = (n: StrategyInputNode) =>
        optimisationCodeOf(String(n.name ?? '')) !== null ||
        optimisationCodeOf(String((n as Record<string, unknown>).x ?? '')) !== null;

    // Group rule indicators + connections by their rule-number prefix ("r.2.SMA.short" -> rule 2).
    // Connection nodes fall back to their x ref. Optimisation nodes are grouped separately.
    const ruleNumOf = (n: StrategyInputNode): number => {
        const fromName = /^(?:r\.)?(\d+)\./.exec(String(n.name ?? ''));
        if (fromName) {
            return Number(fromName[1]);
        }
        const fromX = /^(?:r\.)?(\d+)\./.exec(String((n as Record<string, unknown>).x ?? ''));
        return fromX ? Number(fromX[1]) : 1;
    };
    const ruleMap = new Map<number, NodeGroup>();
    const ensureRule = (num: number): NodeGroup => {
        let group = ruleMap.get(num);
        if (!group) {
            group = { indicators: [], connections: [] };
            ruleMap.set(num, group);
        }
        return group;
    };
    for (const n of indicators) {
        if (!isOptNode(n)) {
            ensureRule(ruleNumOf(n)).indicators.push(n);
        }
    }
    for (const n of connections) {
        if (!isOptNode(n)) {
            ensureRule(ruleNumOf(n)).connections.push(n);
        }
    }
    const ruleGroups: RuleGroup[] = [...ruleMap.keys()]
        .sort((a, b) => a - b)
        .map((num) => ({ num, ...(ruleMap.get(num) as NodeGroup) }));

    // Group optimisation nodes by their embedded code ("o.<code>.*").
    const codeOf = (n: StrategyInputNode): string | null =>
        optimisationCodeOf(String(n.name ?? '')) ?? optimisationCodeOf(String((n as Record<string, unknown>).x ?? ''));
    const optMap = new Map<string, NodeGroup>();
    const ensureOpt = (code: string): NodeGroup => {
        let group = optMap.get(code);
        if (!group) {
            group = { indicators: [], connections: [] };
            optMap.set(code, group);
        }
        return group;
    };
    for (const n of indicators) {
        const code = codeOf(n);
        if (code) {
            ensureOpt(code).indicators.push(n);
        }
    }
    for (const n of connections) {
        const code = codeOf(n);
        if (code) {
            ensureOpt(code).connections.push(n);
        }
    }
    const optimisationGroups: OptimisationGroup[] = [...optMap.keys()]
        .sort()
        .map((code) => ({ code, ...(optMap.get(code) as NodeGroup) }));

    // Indicator display codes (stripped), longest first so a connection ref like
    // "SMA.short_sma" resolves to the most specific indicator name.
    const indicatorCodes = indicators
        .map((n) => stripPrefix(String(n.name ?? '')))
        .filter((n) => n.length > 0)
        .sort((a, b) => b.length - a.length);

    // Resolve a connection ref string (e.g. "1.ALLIGATOR_alligatorLips") to its indicator code,
    // display label, and output portion.
    const resolveRefParts = (ref: unknown): { code: string; label: string; output: string } => {
        const stripped = typeof ref === 'string' ? stripPrefix(ref) : '';
        const code = indicatorCodes.find((n) => stripped.startsWith(n)) ?? stripped;
        const inst = indicatorInstances[code];
        const label = inst?.shortDisplayName ?? inst?.displayName ?? code;
        const output = stripped.length > code.length ? stripped.slice(code.length + 1) : '';
        return { code, label, output };
    };

    const describeConnection = (conn: StrategyInputNode): string => {
        const fields = fieldsForNode(conn);
        const operands: unknown[] = [];
        let operator = '';
        if (fields) {
            for (const f of fields) {
                const v = conn[f.name];
                if (f.referencesNode) {
                    operands.push(v);
                } else if (!f.hidden && typeof v === 'string' && f.name !== 'name' && f.name !== 'type' && !operator) {
                    operator = v;
                }
            }
        }
        if (operands.length === 0) {
            operands.push(conn.x, conn.y);
        }
        if (!operator && typeof conn.mathSign === 'string') {
            operator = conn.mathSign;
        }
        const parts = operands.map(resolveRefParts).filter((p) => p.label.length > 0);
        const type = String(conn.type ?? '');
        const verb = type === 'CrossoverIF' ? `crosses ${operator}`.trim() : operator;
        // Prefix with the connector's shortName, matching the Create wizard.
        const shortName = nodeTemplates[type]?.shortName ?? type;
        // lagX applies to operand x (parts[0]), lagY to operand y (parts[1]); lag 1 = previous bar.
        const lagX = Number(conn.lagX ?? 0);
        const lagY = Number(conn.lagY ?? 0);
        const operand = (p: { label: string; output: string }, lag: number) => {
            const base = p.output ? `${p.label}.${p.output}` : p.label;
            return lag === 1 ? `(prev) ${base}` : base;
        };
        const body =
            parts.length >= 2
                ? `${operand(parts[0], lagX)} ${verb} ${operand(parts[1], lagY)}`
                : parts.map((p) => operand(p, lagX)).join(' ');
        return `${shortName} ${body}`.replace(/\s+/g, ' ').trim();
    };

    return { container, ruleGroups, optimisationGroups, fieldsForNode, nodeLabel, describeConnection };
}
